/**
 * The folder column beside the library.
 *
 * The folders are toggle pills, the same control the queue filters and the "my
 * tickets" switch use, laid out down the page instead of across it. They were
 * hand-written rounded rectangles with a tinted primary background for the
 * selected one - a fourth way of saying "this is the one you are looking at" on
 * a screen that already had three.
 */
import { useState } from "react";
import { Folder, FolderOpen, FolderPlus, Plus } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { Btn, IconBtn } from "../../ui";

export function FolderRail({
  folders,
  active,
  onSelect,
  onCreate,
  folderLabel,
}: {
  folders: string[];
  /** "" means every folder, which is the view the screen opens on. */
  active: string;
  onSelect: (folder: string) => void;
  onCreate: (name: string) => void | Promise<void>;
  folderLabel: (f: string) => string;
}) {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) return;
    await onCreate(trimmed);
    setName("");
    setAdding(false);
  }

  return (
    // Pinned to the same offset as the sidebar, so scrolling a long library does
    // not scroll away the thing you filter it with.
    <aside className="self-start lg:sticky lg:top-[var(--crm-nav-top)] lg:max-h-[calc(var(--crm-main-h)-var(--crm-nav-top)-1rem)] lg:overflow-y-auto lg:pr-1">
      <div className="crm-list-head grid-cols-[1fr_auto] px-2 pb-2 pt-0">
        <span>{t("media.folders")}</span>
        <IconBtn
          small
          onClick={() => setAdding((v) => !v)}
          aria-expanded={adding}
          title={t("media.folderCreate")}
          aria-label={t("media.folderCreate")}
        >
          <FolderPlus className="size-4" />
        </IconBtn>
      </div>

      {adding ? (
        <div className="mb-2 flex items-center gap-1.5">
          <label className="crm-fld h-[38px] min-w-0 flex-1 px-4">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("media.folderName")}
              onKeyDown={(e) => {
                if (e.key === "Enter") void submit();
                if (e.key === "Escape") setAdding(false);
              }}
              autoFocus
            />
          </label>
          <Btn small round tone="brand" onClick={() => void submit()} aria-label={t("media.folderCreate")}>
            <Plus className="size-4" />
          </Btn>
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <FolderPill
          label={t("media.allFolders")}
          on={active === ""}
          onClick={() => onSelect("")}
          icon={<FolderOpen className="size-3.5 shrink-0" />}
        />
        {folders.map((f) => (
          <FolderPill
            key={f}
            label={folderLabel(f)}
            on={active === f}
            onClick={() => onSelect(f)}
            icon={
              active === f ? <FolderOpen className="size-3.5 shrink-0" /> : <Folder className="size-3.5 shrink-0" />
            }
          />
        ))}
      </div>
    </aside>
  );
}

function FolderPill({
  label,
  on,
  onClick,
  icon,
}: {
  label: string;
  on: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      title={label}
      // `justify-start` and `w-full` beat `.crm-cb-wide`'s own centring: these
      // are utilities and the crm classes live in the components layer.
      className={cn("crm-cb crm-cb-wide w-full justify-start", on && "is-on")}
    >
      {icon}
      <span className="crm-trunc">{label}</span>
    </button>
  );
}
